import React, { useEffect, useState, useCallback } from 'react'
import { CircularProgress, Tabs, Tab, Link, Tooltip, useTheme } from '@material-ui/core'
import NotInterestedIcon from '@material-ui/icons/NotInterested'
import DoneIcon from '@material-ui/icons/Done'
import GrainIcon from '@material-ui/icons/Grain'
import UnarchiveIcon from '@material-ui/icons/Unarchive'
import moment from 'moment'
import localization from 'moment/locale/pt-br'

import { Page, Header, CustomTable } from 'components'
import { useVestigioService } from 'hooks/useVestigioService'
import { useSolicitacaoVestigioService } from 'hooks/useSolicitacaoVestigioService'
import { useCestaOperacaoLote } from 'hooks/useCestaOperacaoLote'
import { useAuth } from 'hooks/useAuth'
import { getLabelStatusSolicitacaoVestigio, getStatusCadeiaCustodiaVestigio } from 'helpers/labels'
import useRouter from 'utils/useRouter'
import { useStyles } from './VestigiosDoSetor.styles'

const sortSolicitacao = (a, b) => {
  if (moment(a.data).isSame(b.data)) {
    return a.createdAt < b.createdAt ? -1 : 1
  }
  return a.data < b.data ? -1 : 1
}

const getLabelStatusComUsuario = (rowData) => {
  if (rowData.statusCadeiaCustodia === 'COM_USUARIO') {
    return ` - ${rowData.usuAtual?.nome?.toUpperCase() || ''}`
  }
  return ''
}

const isDisabled = (rowData, newStatus) => {
  switch (newStatus) {
    case 'RECUSADA':
      return rowData.status !== 'PENDENTE'
    case 'EM_SEPARACAO':
      return rowData.status !== 'PENDENTE'
    case 'PRONTA_PARA_COLETA':
      return rowData.status !== 'EM_SEPARACAO'
    case 'ATENDIDA':
      return rowData.status !== 'PRONTA_PARA_COLETA'
    default:
      return true
  }
}

export const VestigiosDoSetor = (props) => {
  const { route } = props
  const { socket } = useAuth()
  const vestigioService = useVestigioService()
  const solicitacaoVestigioService = useSolicitacaoVestigioService()
  const classes = useStyles()
  const { addVestigio, verificarItemEmCesta } = useCestaOperacaoLote()
  const theme = useTheme()
  const router = useRouter()
  moment.locale('pt-br', localization)

  const [tabValue, setTabValue] = useState(0)
  const [vestigiosDoSetor, setVestigiosDoSetor] = useState([])
  const [vestigiosDoSetorEncaminhados, setVestigiosDoSetorEncaminhados] = useState([])
  const [solicitacoesVestigio, setSolicitacoesVestigio] = useState([])

  const [processandoVestigios, setProcessandoVestigios] = useState(false)
  const [processandoSolicitacoes, setProcessandoSolicitacoes] = useState(false)

  const fetchVestigiosDoSetor = useCallback(async () => {
    try {
      setProcessandoVestigios(true)
      const responseDoSetor = await vestigioService.listarVestigiosDoSetorAsync()
      setVestigiosDoSetor(responseDoSetor || [])
    } catch (error) {
      console.error('Erro ao buscar vestígios do setor:', error)
    } finally {
      setProcessandoVestigios(false)
    }
  }, [])

  const fetchVestigiosEncaminhados = useCallback(async () => {
    try {
      setProcessandoVestigios(true)
      const responseEncaminhado = await vestigioService.listarVestigiosEncaminhadosParaSetoresDoUsuarioAsync()
      setVestigiosDoSetorEncaminhados(responseEncaminhado || [])
    } catch (error) {
      console.error('Erro ao buscar vestígios encaminhados do setor:', error)
    } finally {
      setProcessandoVestigios(false)
    }
  }, [])

  const fetchSolicitacoesVestigio = useCallback(async () => {
    try {
      setProcessandoSolicitacoes(true)
      const responseSolicitacoes = await solicitacaoVestigioService.listarSolicitacoesVestigio()
      setSolicitacoesVestigio(responseSolicitacoes || [])
    } catch (error) {
      console.error('Erro ao buscar solicitações do setor:', error)
    } finally {
      setProcessandoSolicitacoes(false)
    }
  }, [])

  useEffect(() => {
    if (tabValue === 0) fetchSolicitacoesVestigio()
    if (tabValue === 1) fetchVestigiosEncaminhados()
    if (tabValue === 2) fetchVestigiosDoSetor()
  }, [tabValue, fetchSolicitacoesVestigio, fetchVestigiosEncaminhados, fetchVestigiosDoSetor])

  useEffect(() => {
    if (socket) {
      socket.on('atualizarSolicitacoes', () => {
        fetchSolicitacoesVestigio()
      })
    }
  }, [socket, fetchSolicitacoesVestigio])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleRowClick = (rowData) => {
    router.history.push(`/casos/caso/vestigio/detalhar/${rowData.id}`)
  }

  const redirectLote = useCallback((id) => {
    router.history.push(`/vestigios/vestigiossetor/lote/${id}`)
  }, [])

  const criarLote = useCallback(async (id) => {
    try {
      setProcessandoVestigios(true)
      const response = await vestigioService.criarLote(id)
      router.history.push(`/vestigios/vestigiossetor/lote/${response.id}`)
    } catch (error) {
      console.error('Erro ao criar lote:', error)
    } finally {
      setProcessandoVestigios(false)
    }
  }, [])

  const handleAlterarStatus = async (id, status) => {
    try {
      await solicitacaoVestigioService.alterarStatusSolicitacao(id, status, setProcessandoVestigios)
      fetchSolicitacoesVestigio()
    } catch (error) {
      console.error('Erro ao alterar status da solicitação:', error)
    }
  }

  const colunasEncaminhados = [
    {
      title: 'Identificador',
      field: 'identificador',
      cellStyle: { fontWeight: 600 },
      customSort: (a, b) => a.identificador.localeCompare(b.identificador),
    },
    {
      title: 'Lacre',
      field: 'lacre',
      render: (rowData) => rowData.lacre || '-',
      customSort: (a, b) => a.lacre.localeCompare(b.lacre),
    },
    {
      title: 'Lote',
      width: '10%',
      render: (rowData) =>
        rowData.lote ? (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              redirectLote(rowData.lote.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            {rowData.lote.numero}
          </Link>
        ) : (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              criarLote(rowData.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            Criar lote
          </Link>
        ),
      customSort: (a, b) => a.lote?.numero.localeCompare(b.lote?.numero),
    },
    {
      title: 'Natureza',
      cellStyle: { minWidth: '250px' },
      render: (rowData) => {
        const gen = rowData.tiposGens?.map((t) => t.nome?.toUpperCase()).join(', ')
        const esp = rowData.tiposEsps?.map((t) => t.nome?.toUpperCase()).join(', ')
        if (gen && esp) return `${gen} / ${esp}`
        return gen || esp || '-'
      },
      customSort: (a, b) =>
        a.tiposGens
          ?.map((t) => t.nome?.toUpperCase())
          .join(', ')
          .localeCompare(b.tiposGens?.map((t) => t.nome?.toUpperCase()).join(', ')),
    },
    {
      title: 'Descrição',
      render: (rowData) => (
        <Tooltip title={rowData.descricao?.toUpperCase() || 'SEM DESCRIÇÃO'} arrow placement="top-start">
          <div style={{
            maxWidth: '380px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: theme.palette.text.secondary,
          }}>{rowData.descricao?.toUpperCase() || '-'}</div>
        </Tooltip>),
      customSort: (a, b) => a.descricao.localeCompare(b.descricao),
    },
    {
      title: 'Setor atual',
      render: (rowData) => (
        <Tooltip title={rowData.setAtual?.nome.toUpperCase() + ' / ' + rowData.setAtual?.divisao.sigla.toUpperCase() + ' / ' + rowData.setAtual?.divisao.regional.sigla.toUpperCase() || 'NÃO INFORMADO'} arrow placement="top-start">
          <div style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: theme.palette.text.secondary,
          }}>{rowData.setAtual?.nome.toUpperCase() + ' / ' + rowData.setAtual?.divisao.sigla.toUpperCase() + ' / ' + rowData.setAtual?.divisao.regional.sigla.toUpperCase() || '-'}</div>
        </Tooltip>),
      customSort: (a, b) => a.setAtual?.nome.localeCompare(b.setAtual?.nome),
    },
    {
      title: 'Data',
      field: 'createdAt',
      render: (rowData) => (rowData.createdAt ? moment(rowData.createdAt).format('DD/MM/YYYY HH:mm') : '-'),
      customSort: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
  ]

  const colunasNoSetor = [
    {
      title: 'Identificador',
      field: 'identificador',
      cellStyle: { fontWeight: 600 },
      customSort: (a, b) => a.identificador.localeCompare(b.identificador),
    },
    {
      title: 'Lacre',
      field: 'lacre',
      render: (rowData) => rowData.lacre || '-',
      customSort: (a, b) => a.lacre?.localeCompare(b.lacre),
    },
    {
      title: 'Lote',
      customSort: (a, b) => a.lote?.numero.localeCompare(b.lote?.numero),
      width: '10%',
      render: (rowData) =>
        rowData.lote ? (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              redirectLote(rowData.lote.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            {rowData.lote.numero}
          </Link>
        ) : (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              criarLote(rowData.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            Criar lote
          </Link>
        ),
    },
    {
      title: 'Natureza',
      cellStyle: { minWidth: '250px' },
      render: (rowData) => {
        const gen = rowData.tiposGens?.map((t) => t.nome?.toUpperCase()).join(', ')
        const esp = rowData.tiposEsps?.map((t) => t.nome?.toUpperCase()).join(', ')
        if (gen && esp) return `${gen} / ${esp}`
        return gen || esp || '-'
      },
      customSort: (a, b) =>
        a.tiposGens
          ?.map((t) => t.nome?.toUpperCase())
          .join(', ')
          .localeCompare(b.tiposGens?.map((t) => t.nome?.toUpperCase()).join(', ')),
    },
    {
      title: 'Descrição',
      render: (rowData) => (<Tooltip title={rowData.descricao?.toUpperCase() || 'SEM DESCRIÇÃO'} arrow placement="top-start">
        <div style={{
          maxWidth: '380px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: theme.palette.text.secondary,
        }}>{rowData.descricao?.toUpperCase() || '-'}</div>
      </Tooltip>),
      customSort: (a, b) => a.descricao.localeCompare(b.descricao),
    },
    {
      title: 'Setor atual',
      render: (rowData) => (<Tooltip title={rowData.setAtual?.nome.toUpperCase() + ' / ' + rowData.setAtual?.divisao.sigla.toUpperCase() + ' / ' + rowData.setAtual?.divisao.regional.sigla.toUpperCase() || 'NÃO INFORMADO'} arrow placement="top-start">
        <div style={{
          maxWidth: '200px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: theme.palette.text.secondary,
        }}>{rowData.setAtual?.nome.toUpperCase() + ' / ' + rowData.setAtual?.divisao.sigla.toUpperCase() + ' / ' + rowData.setAtual?.divisao.regional.sigla.toUpperCase() || '-'}</div>
      </Tooltip>),
      customSort: (a, b) => a.setAtual.nome.localeCompare(b.setAtual.nome),
    },
    {
      title: 'Status',
      render: (rowData) => <Tooltip title={getStatusCadeiaCustodiaVestigio(rowData) + ' ' + getLabelStatusComUsuario(rowData)} arrow placement="top-start">
        <div style={{
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: theme.palette.text.secondary,
        }}>{getStatusCadeiaCustodiaVestigio(rowData) + ' ' + getLabelStatusComUsuario(rowData)}</div>
      </Tooltip>,
      customSort: (a, b) => getStatusCadeiaCustodiaVestigio(a).localeCompare(getStatusCadeiaCustodiaVestigio(b)),
    },
    {
      title: 'Data',
      field: 'createdAt',
      render: (rowData) => (rowData.createdAt ? moment(rowData.createdAt).format('DD/MM/YYYY HH:mm') : '-'),
      customSort: (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
    },
  ]

  const colunasSolicitados = [
    {
      title: 'Setor Solicitante',
      field: 'setSol.nome',
      sorting: true,
    },
    {
      title: '# Vestígios',
      width: '5%',
      render: (rowData) => rowData.vestigios?.length || '-',
      customSort: (a, b) => a.vestigios?.length - b.vestigios?.length,
    },
    {
      title: 'Horário de Solicitação',
      field: 'createdAt',
      customSort: (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
      render: (rowData) => (rowData.createdAt ? moment(rowData.createdAt).fromNow() : '-'),
    },
    {
      title: 'Horário para Atendimento',
      field: 'data',
      render: (rowData) => (rowData.data ? moment(rowData.data).calendar() : '-'),
      customSort: (a, b) => moment(a.data).valueOf() - moment(b.data).valueOf(),
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData) => getLabelStatusSolicitacaoVestigio(rowData.status),
      customSort: (a, b) => a.status.localeCompare(b.status),
    },
  ]

  const acoesCesta = [
    (rowData) => ({
      icon: () => <img src="/images/cesta-add-black.png" alt="Adicionar à cesta" className="img-icon-invert-dark" style={{ width: 24, height: 24 }} />,
      tooltip: 'Adicionar à cesta',
      onClick: (event, rowData) => addVestigio(rowData),
      hidden: verificarItemEmCesta(rowData.id),
    }),
  ]

  const acoesSolicitados = [
    (rowData) => ({
      icon: () => <NotInterestedIcon />,
      tooltip: 'Recusar solicitação',
      onClick: (event, rowData) => handleAlterarStatus(rowData.id, 'RECUSADA'),
      hidden: verificarItemEmCesta(rowData.id),
      disabled: isDisabled(rowData, 'RECUSADA'),
    }),
    (rowData) => ({
      icon: () => <GrainIcon />,
      tooltip: 'Separar',
      onClick: (event, rowData) => handleAlterarStatus(rowData.id, 'EM_SEPARACAO'),
      hidden: verificarItemEmCesta(rowData.id),
      disabled: isDisabled(rowData, 'EM_SEPARACAO'),
    }),
    (rowData) => ({
      icon: () => <UnarchiveIcon />,
      tooltip: 'Pronta para coleta',
      onClick: (event, rowData) => handleAlterarStatus(rowData.id, 'PRONTA_PARA_COLETA'),
      hidden: verificarItemEmCesta(rowData.id),
      disabled: isDisabled(rowData, 'PRONTA_PARA_COLETA'),
    }),
    (rowData) => ({
      icon: () => <img src="/images/cesta-add-black.png" alt="Adicionar à cesta" className="img-icon-invert-dark" style={{ width: 24, height: 24 }} />,
      tooltip: 'Adicionar todos à cesta',
      onClick: (event, rowData) =>
        rowData.vestigios?.forEach((v) => {
          if (!verificarItemEmCesta(v.id)) addVestigio(v)
        }),
      hidden: rowData.vestigios?.every((v) => verificarItemEmCesta(v.id)),
    }),
    (rowData) => ({
      icon: () => <DoneIcon />,
      tooltip: 'Marcar como atendida',
      onClick: (event, rowData) => handleAlterarStatus(rowData.id, 'ATENDIDA'),
      hidden: verificarItemEmCesta(rowData.id),
      disabled: isDisabled(rowData, 'ATENDIDA'),
    }),
  ]

  const estaCarregando = tabValue === 0 ? processandoSolicitacoes : processandoVestigios

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />

      <div className={classes.tabsContainer}>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" aria-label="Abas de Filtros de Vestígios do Setor">
          <Tab label="Solicitados ao setor" />
          <Tab label="Encaminhados para o setor" />
          <Tab label="No setor" />
        </Tabs>
      </div>

      {estaCarregando ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : tabValue === 0 ? (
        <CustomTable columns={colunasSolicitados} data={solicitacoesVestigio} actions={acoesSolicitados} searchPlaceholder="Buscar solicitações..." emptyDataSourceMessage="Nenhuma solicitação encontrada" />
      ) : tabValue === 1 ? (
        <CustomTable columns={colunasEncaminhados} data={vestigiosDoSetorEncaminhados} actions={acoesCesta} onRowClick={(event, rowData) => handleRowClick(rowData)} searchPlaceholder="Buscar vestígios..." emptyDataSourceMessage="Nenhum vestígio encaminhado para o setor encontrado" />
      ) : (
        <CustomTable columns={colunasNoSetor} data={vestigiosDoSetor} actions={acoesCesta} onRowClick={(event, rowData) => handleRowClick(rowData)} searchPlaceholder="Buscar vestígios..." emptyDataSourceMessage="Nenhum vestígio no setor encontrado" />
      )}
    </Page>
  )
}
